import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { supabase } from '@maestra/core/lib/supabase';
import { useAppDispatch } from '@maestra/core/store/store';
import { authActions } from '@maestra/core/store/slices/auth';
import { authError, erroDoEmail } from './erros';

// ENTRAR COM E-MAIL E SENHA — a lógica do antigo `pages/Login`, sem o desenho.

export const useEntrar = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // O convite chega como /login?email=... — quem foi convidado não digita o endereço de novo,
  // e não corre o risco de entrar com outra conta, que não teria convite nenhum.
  const [params] = useSearchParams();
  const [email, setEmail] = useState(() => params.get('email') || '');
  // Quem abriu uma playlist "só a equipe" sem estar logado volta para ela depois de entrar. Só um
  // destino desta forma é aceito: um `voltar` livre seria um redirecionamento aberto.
  const voltar = params.get('voltar') || '';
  const destino = /^\/ouvir\/[A-Za-z0-9_-]{43}$/.test(voltar) ? voltar : '/artists';
  const [password, setPassword] = useState('');
  // EM DOIS PASSOS (pedido do Lucas, 02/10/2026): primeiro o e-mail, e a senha depois de continuar.
  // Assim as outras formas de entrar ficam à vista no primeiro passo. Quem chega com o e-mail no
  // endereço (o convite) já está no passo da senha.
  const [etapa, setEtapa] = useState<'email' | 'senha'>(() => (params.get('email') && !erroDoEmail(params.get('email') || '') ? 'senha' : 'email'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Usuário que se cadastrou mas nunca confirmou o e-mail: o login cai no passo do código.
  const [needsVerify, setNeedsVerify] = useState(false);

  // O erro de cada campo aparece embaixo dele (ao sair do campo e no envio); `error` fica para a
  // resposta do servidor (senha errada).
  const [erroEmail, setErroEmail] = useState<string | undefined>();
  const [erroSenha, setErroSenha] = useState<string | undefined>();
  const conferirSenha = () => (password ? undefined : 'Informe sua senha.');

  const entrar = async () => {
    const e = erroDoEmail(email);
    const s = conferirSenha();
    setErroEmail(e);
    setErroSenha(s);
    if (e || s) return;
    setLoading(true);
    setError(null);
    try {
      await dispatch(authActions.signIn({ email: email.trim(), password })).unwrap();
      navigate(destino, { replace: true });
    } catch (err: any) {
      // E-mail ainda não confirmado → passo do código (que reenvia ao abrir).
      if ((err?.message || '').toLowerCase().includes('not confirmed')) {
        setNeedsVerify(true);
        setLoading(false);
        return;
      }

      // O Supabase responde `invalid_credentials` também para conta não confirmada (e para conta
      // do Google, que não tem senha), para não revelar quais e-mails existem. O servidor sabe
      // distinguir, e é só isso que `auth-login-hint` responde: o par está certo E falta confirmar?
      //
      // Só nesse caso o encaminhamento é automático: fazer isso em toda falha jogaria quem errou a
      // senha numa tela de código que nunca chega, e dispararia um e-mail a cada tentativa.
      try {
        const { data } = await supabase.functions.invoke('auth-login-hint', {
          body: { email: email.trim(), password },
        });
        if (data?.needsConfirmation) {
          setNeedsVerify(true);
          setLoading(false);
          return;
        }
      } catch {
        // Indisponível: segue para o erro comum.
      }

      setError(authError(err));
      setLoading(false);
    }
  };

  /** Do e-mail para a senha: só com um endereço válido (o erro aparece no campo, como no envio). */
  const continuar = () => {
    const e = erroDoEmail(email);
    setErroEmail(e);
    if (!e) setEtapa('senha');
  };

  return {
    etapa,
    continuar,
    /** Volta ao e-mail para trocar: a senha digitada e o erro da tentativa anterior saem junto. */
    trocarEmail: () => { setEtapa('email'); setPassword(''); setErroSenha(undefined); setError(null); },
    email,
    // Com o erro à mostra, cada mudança confere de novo, e ele some assim que o campo fica certo.
    setEmail: (v: string) => { setEmail(v); if (erroEmail) setErroEmail(erroDoEmail(v)); },
    password,
    setPassword: (v: string) => { setPassword(v); if (erroSenha) setErroSenha(v ? undefined : 'Informe sua senha.'); },
    loading,
    error,
    erroDoEmail: erroEmail,
    erroDaSenha: erroSenha,
    validarEmail: () => setErroEmail(erroDoEmail(email)),
    validarSenha: () => setErroSenha(conferirSenha()),
    needsVerify,
    entrar,
    aoConfirmar: () => navigate(destino, { replace: true }),
    // Recuperação de senha tem tela própria. Leva o e-mail já digitado.
    esqueci: () => navigate('/esqueci-senha', { state: { email: email.trim() } }),
    // O cadastro leva o endereço adiante: sem isso o convidado digitaria de novo, e um erro de
    // digitação o levaria a uma conta sem o convite.
    linkDoCadastro: email.trim() ? `/signup?email=${encodeURIComponent(email.trim())}` : '/signup',
  };
};
